import React, { useEffect, useState } from "react";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { affiliatesApi } from "@/data/api";

const STATUS_COLOR = { active: "#3B7A57", invited: "#B07D48", deactivated: "#B23A48" };

export default function AdminAffiliates() {
  const { colors } = useTheme();
  const { token } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    affiliatesApi.list(token).then((r) => setItems(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [token]);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await affiliatesApi.invite({ email: email.trim(), name: name.trim() }, token); toast.show("Invite sent ✨"); setEmail(""); setName(""); load(); }
    catch (e) { toast.show(e.message || "Couldn't invite"); }
    finally { setBusy(false); }
  };

  const act = async (fn, ok) => { try { await fn(); toast.show(ok); load(); } catch (e) { toast.show(e.message || "Action failed"); } };

  const input = { width: "100%", background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "11px 12px", fontSize: 14, color: colors.onSurface, fontFamily: fonts.text, outline: "none", boxSizing: "border-box", marginBottom: 10 };

  return (
    <Screen>
      <TopBar title="Affiliates" subtitle="Invite and manage RitzyRa reps" />
      <div className="ritzy-fade" style={{ padding: "16px 20px 40px" }}>
        <div style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 16, marginBottom: 20 }}>
          <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 700, marginBottom: 12, fontFamily: fonts.text }}>Invite a new affiliate</div>
          <input data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" type="email" style={input} />
          <input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={input} />
          <button data-testid="invite-submit-button" className="pressable" onClick={invite} disabled={busy} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "13px", cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text, opacity: busy ? 0.7 : 1 }}>
            <Icon name="send" size={15} color={colors.onBrand} />Send invite
          </button>
        </div>

        {loading ? <Loader /> : (
          <div>
            <div style={{ color: colors.muted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, fontFamily: fonts.text }}>{items.length} people</div>
            {items.map((a) => (
              <div key={a.email} data-testid={`affiliate-${a.email}`} style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>{a.name || a.email}{a.is_admin ? " · Owner" : ""}</div>
                    <div style={{ color: colors.muted, fontSize: 12, fontFamily: fonts.text }}>{a.email}</div>
                  </div>
                  <span style={{ background: (STATUS_COLOR[a.status] || colors.muted) + "22", color: STATUS_COLOR[a.status] || colors.muted, borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", fontFamily: fonts.text }}>{a.status}</span>
                </div>
                {!a.is_admin ? (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {a.status === "invited" ? <button className="pressable" onClick={() => act(() => affiliatesApi.resend(a.email, token), "Invite resent")} style={btn(colors)}>Resend</button> : null}
                    {a.status === "deactivated" ? <button className="pressable" onClick={() => act(() => affiliatesApi.activate(a.email, token), "Activated")} style={btn(colors)}>Activate</button> : null}
                    {a.status === "active" ? <button className="pressable" onClick={() => act(() => affiliatesApi.deactivate(a.email, token), "Deactivated")} style={btn(colors)}>Deactivate</button> : null}
                    <button className="pressable" onClick={() => { if (window.confirm(`Revoke access for ${a.email}?`)) act(() => affiliatesApi.revoke(a.email, token), "Access revoked"); }} style={{ ...btn(colors), color: colors.error, borderColor: colors.error }}>Revoke</button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}

const btn = (colors) => ({ background: "transparent", border: `1px solid ${colors.border}`, borderRadius: 999, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: colors.onSurface, fontFamily: fonts.text });
